import axios, { AxiosBasicCredentials, AxiosRequestConfig } from 'axios';
import { stringify } from 'qs';
import { useSessionStore } from 'store/session';

export const ROOT_URL = import.meta.env.VITE_APP_BASE_API_URL ?? '/api/';

export interface BasicCredentials extends AxiosBasicCredentials {
  type: 'basic';
}

export interface HeaderCredentials {
  type: 'header';
  name: string;
  value: string;
}

export type Authenticator = () => BasicCredentials | HeaderCredentials;

/**
 * @deprecated Use `customApiKeyAuthenticator` instead.
 */
const globalApiKeyAuthenticator: Authenticator = () => ({
  type: 'basic',
  username: 'InventoryLocation',
  password: useSessionStore.getState().apiKey ?? '',
});

export const customApiKeyAuthenticator =
  (apiKey: string): Authenticator =>
  () => ({
    type: 'basic',
    username: 'InventoryLocation',
    password: apiKey,
  });

export interface RequestConfig extends AxiosRequestConfig {
  /**
   * If set to null then request will be send without authorization header.
   * @default globalApiKeyAuthenticator
   */
  authenticator?: null | Authenticator;
}

export class HttpService {
  protected readonly urlPrefix: string;

  constructor(
    urlPrefix: string,
    private authenticator?: Authenticator,
  ) {
    this.urlPrefix = urlPrefix;
  }

  async get<T>(url: string, config: RequestConfig = {}): Promise<T> {
    const response = await this.getAxios(config).get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, body: unknown, config: RequestConfig = {}): Promise<T> {
    const response = await this.getAxios(config).post<T>(url, body, config);
    return response.data;
  }

  async put<T>(url: string, body: unknown, config: RequestConfig = {}): Promise<T> {
    const response = await this.getAxios(config).put<T>(url, body, config);
    return response.data;
  }

  async delete<T>(url: string, options: RequestConfig = {}): Promise<T> {
    const response = await this.getAxios(options).delete<T>(url);
    return response.data;
  }

  private getAxios({ authenticator = this.authenticator ?? globalApiKeyAuthenticator }: RequestConfig) {
    const credentials = authenticator?.();

    return axios.create({
      baseURL: ROOT_URL + this.urlPrefix,
      auth: credentials?.type === 'basic' ? { ...credentials } : undefined,
      headers: {
        ...(credentials?.type === 'header'
          ? {
              [credentials.name]: credentials.value,
            }
          : {}),
      },
      paramsSerializer: params => (typeof params === 'string' ? params : stringify(params)),
    });
  }
}
